import { TrendingDown, TrendingUp } from '@mui/icons-material';
import {
  Box,
  Card,
  CardContent,
  FormControl,
  Grid,
  MenuItem,
  Select,
  Typography,
} from '@mui/material';
import React, { useEffect, useState } from 'react';
import {
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

import { formatMoney } from '../../../../../../../../lib/functions/utils';

const colorsArray = [
  '#FF1744', // totalColocado - rojo
  '#07D2FE', // totalColocadoNeto - azul claro
  '#B8336A', // ingresoTotal - rosa
  '#0F23DF', // costoFondo - azul oscuro
  '#FFC400', // utilidad - amarillo
];

interface IncomeBookData {
  date: string;
  totalWallet: number;
  totalWalletNet: number;
  totalPriceIncome: number;
  totalArrearIncome: number;
  totalComissionIncome: number;
  totalExpensesIncome: number;
  totalDocumentExtensionIncome: number;
  totalIncome: number;
  totalDocumentResellExpense: number;
}

interface ProfitLossChartProps {
  data: IncomeBookData[];
  loading?: boolean;
}

const ProfitLossChart: React.FC<ProfitLossChartProps> = ({
  data,
  loading = false,
}) => {
  const [viewMode, setViewMode] = useState<'daily' | 'monthly'>('daily');
  const [chartData, setChartData] = useState<any[]>([]);
  const [visibleLines, setVisibleLines] = useState<string[]>([
    'ingresoTotal',
    'costoFondo',
    'utilidad',
  ]); // Solo mostrar ingreso, costo y utilidad por defecto
  const [summaryData, setSummaryData] = useState({
    totalColocado: 0,
    totalColocadoNeto: 0,
    ingresoTotal: 0,
    costoFondo: 0,
    utilidad: 0,
  });

  // Determinar si se puede mostrar la vista mensual (necesita al menos 2 meses)
  const canShowMonthly = () => {
    if (!data || data.length === 0) return false;

    const uniqueMonths = new Set();
    data.forEach((item) => {
      const month = new Date(item.date).toISOString().substring(0, 7); // YYYY-MM
      uniqueMonths.add(month);
    });

    return uniqueMonths.size >= 2;
  };

  // Función para formatear fechas de manera más legible
  const formatDate = (dateString: string, isMonthly: boolean = false) => {
    // Crear la fecha considerando que viene en formato YYYY-MM-DD
    const [year, month, day] = dateString.split('-').map(Number);
    const date = new Date(year, month - 1, day); // month - 1 porque los meses van de 0-11

    if (isMonthly) {
      return date.toLocaleDateString('es-CL', {
        year: 'numeric',
        month: 'long',
      });
    } else {
      return date.toLocaleDateString('es-CL', {
        day: '2-digit',
        month: '2-digit',
      });
    }
  };

  // Procesar datos para el gráfico
  const processChartData = () => {
    if (!data || data.length === 0) {
      setChartData([]);
      return;
    }

    let processedData: any[] = [];

    if (viewMode === 'daily') {
      // Agrupar por día
      const dailyMap = new Map();

      data.forEach((item) => {
        const date = formatDate(item.date, false);

        if (dailyMap.has(date)) {
          const existing = dailyMap.get(date);
          dailyMap.set(date, {
            name: date,
            totalColocado: existing.totalColocado + item.totalWallet,
            totalColocadoNeto: existing.totalColocadoNeto + item.totalWalletNet,
            ingresoTotal: existing.ingresoTotal + item.totalIncome,
            costoFondo: existing.costoFondo + item.totalDocumentResellExpense,
            utilidad:
              existing.utilidad +
              (item.totalIncome - item.totalDocumentResellExpense),
          });
        } else {
          dailyMap.set(date, {
            name: date,
            totalColocado: item.totalWallet,
            totalColocadoNeto: item.totalWalletNet,
            ingresoTotal: item.totalIncome,
            costoFondo: item.totalDocumentResellExpense,
            utilidad: item.totalIncome - item.totalDocumentResellExpense,
          });
        }
      });

      processedData = Array.from(dailyMap.values());
    } else {
      // Agrupar por mes
      const monthlyMap = new Map();

      data.forEach((item) => {
        const month = formatDate(item.date, true);

        if (monthlyMap.has(month)) {
          const existing = monthlyMap.get(month);
          monthlyMap.set(month, {
            name: month,
            totalColocado: existing.totalColocado + item.totalWallet,
            totalColocadoNeto: existing.totalColocadoNeto + item.totalWalletNet,
            ingresoTotal: existing.ingresoTotal + item.totalIncome,
            costoFondo: existing.costoFondo + item.totalDocumentResellExpense,
            utilidad:
              existing.utilidad +
              (item.totalIncome - item.totalDocumentResellExpense),
          });
        } else {
          monthlyMap.set(month, {
            name: month,
            totalColocado: item.totalWallet,
            totalColocadoNeto: item.totalWalletNet,
            ingresoTotal: item.totalIncome,
            costoFondo: item.totalDocumentResellExpense,
            utilidad: item.totalIncome - item.totalDocumentResellExpense,
          });
        }
      });

      processedData = Array.from(monthlyMap.values());
    }

    setChartData(processedData);
  };

  // Calcular resumen total
  const calculateSummary = () => {
    if (!data || data.length === 0) {
      setSummaryData({
        totalColocado: 0,
        totalColocadoNeto: 0,
        ingresoTotal: 0,
        costoFondo: 0,
        utilidad: 0,
      });
      return;
    }

    const summary = data.reduce(
      (acc, item) => ({
        totalColocado: acc.totalColocado + item.totalWallet,
        totalColocadoNeto: acc.totalColocadoNeto + item.totalWalletNet,
        ingresoTotal: acc.ingresoTotal + item.totalIncome,
        costoFondo: acc.costoFondo + item.totalDocumentResellExpense,
        utilidad:
          acc.utilidad + (item.totalIncome - item.totalDocumentResellExpense),
      }),
      {
        totalColocado: 0,
        totalColocadoNeto: 0,
        ingresoTotal: 0,
        costoFondo: 0,
        utilidad: 0,
      },
    );

    setSummaryData(summary);
  };

  useEffect(() => {
    processChartData();
    calculateSummary();
  }, [data, viewMode]);

  const handleViewModeChange = (event: any) => {
    setViewMode(event.target.value);
  };

  const formatLineMoney = (value: number) => {
    if (value > 1000000000) return `${formatMoney(value / 1000000000)}MM`;
    if (value > 1000000) return `${formatMoney(value / 1000000)}M`;
    if (value > 1000) return `${formatMoney(value / 1000)}K`;
    return formatMoney(value);
  };

  const getLineName = (key: string) => {
    const names: { [key: string]: string } = {
      totalColocado: 'Total Colocado',
      totalColocadoNeto: 'Total Neto',
      ingresoTotal: 'Ingreso Total',
      costoFondo: 'Costo Fondo',
      utilidad: 'Utilidad',
    };
    return names[key] || key;
  };

  const getLineIndex = (key: string) => {
    const indices: { [key: string]: number } = {
      totalColocado: 0,
      totalColocadoNeto: 1,
      ingresoTotal: 2,
      costoFondo: 3,
      utilidad: 4,
    };
    return indices[key] || 0;
  };

  return (
    <Grid item xs={12} md={8}>
      <Card sx={{ height: '100%', minHeight: 300 }}>
        <CardContent sx={{ pb: 1 }}>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            mb={2}
          >
            <Typography variant="h6" component="h2">
              Ingresos y Costos
            </Typography>
            <Box display="flex" gap={2}>
              {/* Dropdown para seleccionar líneas visibles */}
              <FormControl size="small" sx={{ minWidth: 200 }}>
                <Select
                  multiple
                  value={visibleLines}
                  onChange={(e) => {
                    const selected = e.target.value as string[];
                    setVisibleLines(selected);
                  }}
                  displayEmpty
                  renderValue={(selected) => {
                    if (selected.length === 0) {
                      return (
                        <Typography variant="body2" color="text.secondary">
                          Seleccionar líneas
                        </Typography>
                      );
                    }
                    return (
                      <Typography variant="body2">
                        {selected.length} líneas seleccionadas
                      </Typography>
                    );
                  }}
                >
                  <MenuItem value="totalColocado">
                    <Box
                      display="flex"
                      alignItems="center"
                      gap={1}
                      width="100%"
                    >
                      <Box
                        sx={{
                          width: 12,
                          height: 12,
                          backgroundColor: colorsArray[0],
                          borderRadius: '50%',
                        }}
                      />
                      <Typography
                        variant="body2"
                        sx={{
                          flexGrow: 1,
                          textDecoration: visibleLines.includes('totalColocado')
                            ? 'none'
                            : 'line-through',
                          color: visibleLines.includes('totalColocado')
                            ? 'text.primary'
                            : 'text.disabled',
                        }}
                      >
                        {getLineName('totalColocado')}
                      </Typography>
                      {visibleLines.includes('totalColocado') && (
                        <Box
                          sx={{
                            width: 8,
                            height: 8,
                            backgroundColor: 'primary.main',
                            borderRadius: '50%',
                          }}
                        />
                      )}
                    </Box>
                  </MenuItem>
                  <MenuItem value="totalColocadoNeto">
                    <Box
                      display="flex"
                      alignItems="center"
                      gap={1}
                      width="100%"
                    >
                      <Box
                        sx={{
                          width: 12,
                          height: 12,
                          backgroundColor: colorsArray[1],
                          borderRadius: '50%',
                        }}
                      />
                      <Typography
                        variant="body2"
                        sx={{
                          flexGrow: 1,
                          textDecoration: visibleLines.includes(
                            'totalColocadoNeto',
                          )
                            ? 'none'
                            : 'line-through',
                          color: visibleLines.includes('totalColocadoNeto')
                            ? 'text.primary'
                            : 'text.disabled',
                        }}
                      >
                        {getLineName('totalColocadoNeto')}
                      </Typography>
                      {visibleLines.includes('totalColocadoNeto') && (
                        <Box
                          sx={{
                            width: 8,
                            height: 8,
                            backgroundColor: 'primary.main',
                            borderRadius: '50%',
                          }}
                        />
                      )}
                    </Box>
                  </MenuItem>
                  <MenuItem value="ingresoTotal">
                    <Box
                      display="flex"
                      alignItems="center"
                      gap={1}
                      width="100%"
                    >
                      <Box
                        sx={{
                          width: 12,
                          height: 12,
                          backgroundColor: colorsArray[2],
                          borderRadius: '50%',
                        }}
                      />
                      <Typography
                        variant="body2"
                        sx={{
                          flexGrow: 1,
                          textDecoration: visibleLines.includes('ingresoTotal')
                            ? 'none'
                            : 'line-through',
                          color: visibleLines.includes('ingresoTotal')
                            ? 'text.primary'
                            : 'text.disabled',
                        }}
                      >
                        {getLineName('ingresoTotal')}
                      </Typography>
                      {visibleLines.includes('ingresoTotal') && (
                        <Box
                          sx={{
                            width: 8,
                            height: 8,
                            backgroundColor: 'primary.main',
                            borderRadius: '50%',
                          }}
                        />
                      )}
                    </Box>
                  </MenuItem>
                  <MenuItem value="costoFondo">
                    <Box
                      display="flex"
                      alignItems="center"
                      gap={1}
                      width="100%"
                    >
                      <Box
                        sx={{
                          width: 12,
                          height: 12,
                          backgroundColor: colorsArray[3],
                          borderRadius: '50%',
                        }}
                      />
                      <Typography
                        variant="body2"
                        sx={{
                          flexGrow: 1,
                          textDecoration: visibleLines.includes('costoFondo')
                            ? 'none'
                            : 'line-through',
                          color: visibleLines.includes('costoFondo')
                            ? 'text.primary'
                            : 'text.disabled',
                        }}
                      >
                        {getLineName('costoFondo')}
                      </Typography>
                      {visibleLines.includes('costoFondo') && (
                        <Box
                          sx={{
                            width: 8,
                            height: 8,
                            backgroundColor: 'primary.main',
                            borderRadius: '50%',
                          }}
                        />
                      )}
                    </Box>
                  </MenuItem>
                  <MenuItem value="utilidad">
                    <Box
                      display="flex"
                      alignItems="center"
                      gap={1}
                      width="100%"
                    >
                      <Box
                        sx={{
                          width: 12,
                          height: 12,
                          backgroundColor: colorsArray[4],
                          borderRadius: '50%',
                        }}
                      />
                      <Typography
                        variant="body2"
                        sx={{
                          flexGrow: 1,
                          textDecoration: visibleLines.includes('utilidad')
                            ? 'none'
                            : 'line-through',
                          color: visibleLines.includes('utilidad')
                            ? 'text.primary'
                            : 'text.disabled',
                        }}
                      >
                        {getLineName('utilidad')}
                      </Typography>
                      {visibleLines.includes('utilidad') && (
                        <Box
                          sx={{
                            width: 8,
                            height: 8,
                            backgroundColor: 'primary.main',
                            borderRadius: '50%',
                          }}
                        />
                      )}
                    </Box>
                  </MenuItem>
                </Select>
              </FormControl>

              {/* Selector de vista diaria/mensual */}
              <FormControl size="small" sx={{ minWidth: 120 }}>
                <Select
                  value={viewMode}
                  onChange={handleViewModeChange}
                  disabled={!canShowMonthly()}
                >
                  <MenuItem value="daily">Por Días</MenuItem>
                  <MenuItem value="monthly">Por Meses</MenuItem>
                </Select>
              </FormControl>
            </Box>
          </Box>

          {/* Gráfico */}
          <Box sx={{ height: 250, position: 'relative' }}>
            {loading ? (
              <Box
                display="flex"
                justifyContent="center"
                alignItems="center"
                height="100%"
              >
                <Typography>Cargando datos...</Typography>
              </Box>
            ) : chartData.length > 0 ? (
              <>
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart
                    data={chartData}
                    margin={{
                      top: 5,
                      right: 30,
                      left: 20,
                      bottom: 0,
                    }}
                  >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis
                      dataKey="name"
                      angle={-45}
                      textAnchor="end"
                      height={60}
                      fontSize={11}
                    />
                    <YAxis tickFormatter={formatLineMoney} />
                    <Tooltip formatter={formatMoney} />

                    {/* Líneas del gráfico */}
                    <Line
                      type="monotone"
                      dataKey="totalColocado"
                      stroke={colorsArray[0]}
                      name={getLineName('totalColocado')}
                      hide={!visibleLines.includes('totalColocado')}
                      strokeWidth={2}
                    />
                    <Line
                      type="monotone"
                      dataKey="totalColocadoNeto"
                      stroke={colorsArray[1]}
                      name={getLineName('totalColocadoNeto')}
                      hide={!visibleLines.includes('totalColocadoNeto')}
                      strokeWidth={2}
                    />
                    <Line
                      type="monotone"
                      dataKey="ingresoTotal"
                      stroke={colorsArray[2]}
                      name={getLineName('ingresoTotal')}
                      hide={!visibleLines.includes('ingresoTotal')}
                      strokeWidth={2}
                    />
                    <Line
                      type="monotone"
                      dataKey="costoFondo"
                      stroke={colorsArray[3]}
                      name={getLineName('costoFondo')}
                      hide={!visibleLines.includes('costoFondo')}
                      strokeWidth={2}
                    />
                    <Line
                      type="monotone"
                      dataKey="utilidad"
                      stroke={colorsArray[4]}
                      name={getLineName('utilidad')}
                      hide={!visibleLines.includes('utilidad')}
                      strokeWidth={2}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </>
            ) : (
              <Box
                display="flex"
                justifyContent="center"
                alignItems="center"
                height="100%"
              >
                <Typography color="textSecondary">
                  No hay datos disponibles para mostrar
                </Typography>
              </Box>
            )}
          </Box>
        </CardContent>
      </Card>
    </Grid>
  );
};

export default ProfitLossChart;
